import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/shared/components/ui/Badge';
import { ButtonArrow } from '@/shared/components/ui/ButtonArrow';
import { Button } from '@/shared/components/ui/Button';
import { useI18n } from '@/shared/contexts/I18nProvider';

import { scrollToSection } from '../HomeUtils';

const lifecycleStageLabels = [
  {
    key: 'ingest',
    desktopClassName: 'left-[12.5%] -ml-3 -translate-x-full'
  },
  {
    key: 'govern',
    desktopClassName: 'left-[35%] -ml-3 -translate-x-full'
  },
  {
    key: 'operate',
    desktopClassName: 'left-[65%] ml-3'
  },
  {
    key: 'monitor',
    desktopClassName: 'left-[87.5%] ml-3'
  }
] as const;

const lifecycleDesktopFlowRoutes = [
  {
    key: 'ingest',
    linePath: 'M600 320 C585 182 510 62 150 0',
    motionPath: 'M150 0 C510 62 585 182 600 320',
    duration: 6.2,
    delay: 0
  },
  {
    key: 'govern',
    linePath: 'M600 320 C590 176 550 66 420 0',
    motionPath: 'M420 0 C550 66 590 176 600 320',
    duration: 5.6,
    delay: 0.65
  },
  {
    key: 'operate',
    linePath: 'M600 320 C610 176 650 66 780 0',
    motionPath: 'M780 0 C650 66 610 176 600 320',
    duration: 5.6,
    delay: 1.2
  },
  {
    key: 'monitor',
    linePath: 'M600 320 C615 182 690 62 1050 0',
    motionPath: 'M1050 0 C690 62 615 182 600 320',
    duration: 6.2,
    delay: 1.85
  }
] as const;

const lifecycleMobileFlowRoutes = [
  {
    key: 'ingest',
    linePath: 'M195 280 C190 174 152 58 49 0',
    motionPath: 'M49 0 C152 58 190 174 195 280',
    duration: 5.8,
    delay: 0
  },
  {
    key: 'govern',
    linePath: 'M195 280 C192 172 172 64 137 0',
    motionPath: 'M137 0 C172 64 192 172 195 280',
    duration: 5.3,
    delay: 0.55
  },
  {
    key: 'operate',
    linePath: 'M195 280 C198 172 218 64 254 0',
    motionPath: 'M254 0 C218 64 198 172 195 280',
    duration: 5.3,
    delay: 1.05
  },
  {
    key: 'monitor',
    linePath: 'M195 280 C200 174 238 58 341 0',
    motionPath: 'M341 0 C238 58 200 174 195 280',
    duration: 5.8,
    delay: 1.6
  }
] as const;

const lifecyclePulseOffsets = [0, 0.52] as const;

type LifecycleFlowRoute = {
  key: string;
  linePath: string;
  motionPath: string;
  duration: number;
  delay: number;
};

const renderLifecycleGlow = (gradientId: string) => (
  <>
    <defs>
      <radialGradient id={gradientId} cx="50%" cy="100%" r="92%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0.32" />
        <stop offset="48%" stopColor="currentColor" stopOpacity="0.1" />
        <stop offset="78%" stopColor="currentColor" stopOpacity="0.04" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
      </radialGradient>
    </defs>
    <rect width="100%" height="100%" fill={`url(#${gradientId})`} />
  </>
);

const renderLifecycleFlow = (routes: ReadonlyArray<LifecycleFlowRoute>) => (
  <>
    {routes.map(route => (
      <path
        key={route.key}
        d={route.linePath}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeOpacity="0.55"
        vectorEffect="non-scaling-stroke"
      />
    ))}
    {routes.flatMap(route =>
      lifecyclePulseOffsets.map((offset, index) => {
        const begin = route.delay + route.duration * offset;

        return (
          <g
            key={`${route.key}-${offset}`}
            className="lifecycle-workflow-particle"
            opacity="0"
          >
            <circle
              r={index === 0 ? '12' : '9'}
              fill="currentColor"
              opacity="0.16"
            />
            <circle r={index === 0 ? '4.5' : '3.5'} fill="currentColor" />
            <animateMotion
              path={route.motionPath}
              dur={`${route.duration}s`}
              begin={`${begin}s`}
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              values="0;1;1;0"
              keyTimes="0;0.08;0.84;1"
              dur={`${route.duration}s`}
              begin={`${begin}s`}
              repeatCount="indefinite"
            />
          </g>
        );
      })
    )}
  </>
);

export const Hero = () => {
  const navigate = useNavigate();
  const { content, localizedPath } = useI18n();

  const goToContact = () => {
    void navigate(localizedPath('/contact'));
    window.setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  };

  return (
    <section
      id="home"
      className="lifecycle-hero relative overflow-hidden border-b border-border-muted"
    >
      <div className="relative z-10 mx-auto flex min-h-[790px] max-w-7xl flex-col items-center px-4 pt-24 text-center sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          className="flex flex-1 flex-col items-center justify-center pb-12"
        >
          <Badge
            variant="outline"
            className="mb-7 border-accent-primary/30 bg-background-card/70 px-4 py-2 font-mono text-accent-primary shadow-lg shadow-shadow backdrop-blur"
          >
            {content.home.hero.badge}
          </Badge>

          <h1 className="max-w-5xl text-[clamp(3.4rem,8vw,7.25rem)] font-light leading-[0.94] tracking-[-0.06em] text-accent-primary drop-shadow-[0_0_26px_rgba(132,202,255,0.35)]">
            {content.home.hero.title}
          </h1>

          <p className="mt-8 max-w-2xl text-lg leading-8 text-text-secondary sm:text-xl">
            {content.home.hero.descriptionBeforeAccent}
            <span className="text-accent-secondary">
              {content.home.hero.descriptionAccent}
            </span>
            {content.home.hero.descriptionAfterAccent}
          </p>

          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button
              size="lg"
              onClick={() => scrollToSection('platform')}
              className="cursor-pointer gap-2 rounded-md bg-text-primary px-8 font-mono text-text-inverse hover:bg-text-primary/90"
            >
              {content.home.hero.startMapping}
              <ButtonArrow className="h-5 w-5" />
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={goToContact}
              className="cursor-pointer gap-2 rounded-md bg-transparent px-8 font-mono"
            >
              {content.home.hero.getDemo}
            </Button>
          </div>
        </motion.div>

        <div
          id="lifecycle"
          className="relative h-[250px] w-screen max-w-none border-t border-accent-primary/50 sm:h-[270px]"
        >
          <svg
            viewBox="0 0 1200 320"
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-full w-full text-accent-primary"
            preserveAspectRatio="none"
          >
            {renderLifecycleGlow('workflowGlowFullBleed')}
          </svg>

          <div className="pointer-events-none absolute inset-x-3 top-0 z-10 grid grid-cols-4 gap-1 sm:hidden">
            {lifecycleStageLabels.map(stage => (
              <div
                key={stage.key}
                className="-translate-y-1/2 truncate rounded-full border border-accent-primary/35 bg-background-card/85 px-1.5 py-1.5 text-center font-mono text-[0.68rem] uppercase tracking-[0.04em] text-accent-primary shadow-lg shadow-shadow backdrop-blur"
              >
                {content.home.hero.lifecycleStages[stage.key]}
              </div>
            ))}
          </div>

          <div className="pointer-events-none absolute inset-x-0 top-0 z-10 mx-auto hidden h-full max-w-[1200px] sm:block">
            {lifecycleStageLabels.map(stage => (
              <div
                key={stage.key}
                className={`absolute top-0 -translate-y-1/2 rounded-full border border-accent-primary/35 bg-background-card px-5 py-1.5 font-mono text-sm text-accent-primary shadow-lg shadow-shadow backdrop-blur ${stage.desktopClassName}`}
              >
                {content.home.hero.lifecycleStages[stage.key]}
              </div>
            ))}
          </div>

          <svg
            viewBox="0 0 1200 320"
            aria-hidden="true"
            className="absolute left-1/2 top-0 hidden h-full w-[1200px] -translate-x-1/2 text-accent-primary sm:block"
            preserveAspectRatio="none"
          >
            {renderLifecycleFlow(lifecycleDesktopFlowRoutes)}
          </svg>

          <svg
            viewBox="0 0 390 280"
            aria-hidden="true"
            className="absolute left-0 top-0 h-full w-full text-accent-primary sm:hidden"
            preserveAspectRatio="none"
          >
            {renderLifecycleFlow(lifecycleMobileFlowRoutes)}
          </svg>
        </div>
      </div>
    </section>
  );
};
